/**
 * App Component - Root Router
 *
 * Manages route definitions and authentication guards.
 * All page logic has been moved to dedicated page components.
 *
 * ROUTES:
 * - /login          -> LoginPage (public)
 * - /signup         -> SignupPage (public) - registration
 * - /reset-password -> ResetPasswordPage (public) - password reset callback
 * - /auth/callback  -> AuthCallbackPage (public) - invite email callback
 * - /change-password -> ChangePasswordPage (protected, mandatory after a
 *                      support-issued temporary password; skips all guards)
 * - /onboarding     -> OnboardingPage (protected, skip onboarding guard)
 * - /               -> ChatPage (protected) - chat-first entry point
 * - /projects       -> ProjectsPage (protected) - project history
 * - /create         -> CreatePage (protected, legacy — not linked in sidebar)
 * - /editor/:id     -> EditorPage (protected, view/export allowed when paywalled)
 * - /admin          -> AdminDashboardPage (protected, admin only)
 * - /admin/grow-ops -> GrowOpsPage (protected, admin only)
 *
 * NOTE: DashboardPage import is retained for the transition period but no
 * route references it. Remove once ChatPage stabilizes in production.
 */

import React from 'react';
import { Routes, Route, Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppLayout from '@/components/AppLayout';
import LoadingScreen from '@/components/ui/loading-screen';
import PaywallModal from '@/components/PaywallModal';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import OnboardingPage from '@/pages/OnboardingPage';
import DashboardPage from '@/pages/DashboardPage';
import ChatPage from '@/pages/ChatPage';
import ProjectsPage from '@/pages/ProjectsPage';
import CreatePage from '@/pages/CreatePage';
import EditorPage from '@/pages/EditorPage';
import PlansPage from '@/pages/PlansPage';
import AdminDashboardPage from '@/pages/AdminDashboardPage';
import GrowOpsPage from '@/pages/GrowOpsPage';
import ImpersonatePage from '@/pages/ImpersonatePage';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { useUtmCapture } from '@/hooks/useUtmCapture';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const RenderPage = React.lazy(() => import('./pages/RenderPage'));

/**
 * Handles redirect when an already-authenticated user visits /login or /signup.
 * Respects the ?redirect= query parameter so users land on the intended page.
 */
const AuthRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  return <Navigate to={redirectTo} replace />;
};

const App: React.FC = () => {
  const { loading, user, isConfigured } = useAuth();
  const { showPaywall, setShowPaywall } = useUserStatus();

  // Capture UTM parameters from URL on first visit (stores in localStorage)
  useUtmCapture();

  // Show loading while checking initial auth state
  // (skip for /render route since it's public and doesn't require auth)
  const isRenderRoute = typeof window !== 'undefined' && window.location.pathname === '/render';
  if (loading && !isRenderRoute) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Routes>
        {/* Chrome-less server-side render route (public, no auth, no app shell) */}
        <Route path="/render" element={<React.Suspense fallback={null}><RenderPage /></React.Suspense>} />

        {/* Public routes */}
        <Route
          path="/login"
          element={
            // If already logged in, redirect to intended destination or dashboard
            user ? <AuthRedirect /> : <LoginPage />
          }
        />

        <Route
          path="/signup"
          element={
            user ? <AuthRedirect /> : <SignupPage />
          }
        />

        {/* Aba de suporte (impersonate): entrada via #imp=... e aviso de sessão encerrada */}
        <Route path="/impersonate" element={<ImpersonatePage />} />

        {/* Password reset callback (public - accessed via email link) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Invite callback (public - accessed via email link from paid-flow webhook) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Troca obrigatória de senha temporária: sem sidebar, sem outros guards */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute skipPasswordGuard skipOnboardingGuard>
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/meus-planos"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PlansPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Onboarding: protected but skips onboarding guard (it IS the onboarding) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute skipOnboardingGuard>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with sidebar layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ChatPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProjectsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/create"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CreatePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Editor: fullscreen without sidebar */}
        <Route
          path="/editor/:id"
          element={
            <ProtectedRoute>
              <ErrorBoundary>
                <EditorPage />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Admin routes (requires admin role) - shared layout prevents remount on sub-navigation */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AppLayout>
                <Outlet />
              </AppLayout>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="grow-ops" element={<GrowOpsPage />} />
        </Route>

        {/* Catch-all: redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Faixa "Você está como X" da aba de suporte (null fora do impersonate) */}
      <ImpersonationBanner />

      {/* Global paywall modal - rendered outside Routes so it overlays everything */}
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} />
    </>
  );
};

export default App;
